import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import CategoriesManager from "@/components/CategoriesManager";
import { TournamentsAPI, ChampionshipsAPI } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SiteHeader from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { getSport } from "@/lib/sports";
import { Crown, Medal, Plus, Pencil, ExternalLink, LogOut, QrCode } from "lucide-react";

const EventCard = ({ item, kind, index }) => {
  const sportDef = getSport(item.sport || "football");
  const publicPath = kind === "tournament" ? `/tournament/${item.slug}` : `/championship/${item.slug}`;
  const editPath = kind === "tournament" ? `/admin/tournaments/${item.id}` : `/admin/championships/${item.id}`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.05 }}
      className="bg-[#111216] border border-white/10 rounded-xl p-4 flex items-center gap-4"
      data-testid={`organizer-${kind}-${item.slug}`}
    >
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
        style={{
          background: `${item.primary_color || "#D4AF37"}15`,
          border: `1px solid ${item.primary_color || "#D4AF37"}40`,
        }}
      >
        {kind === "tournament" ? (
          <Medal className="w-5 h-5" style={{ color: item.primary_color || "#D4AF37" }} />
        ) : (
          <Crown className="w-5 h-5" style={{ color: item.primary_color || "#D4AF37" }} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display text-lg font-bold uppercase tracking-tight truncate">
          {item.name}
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
          {sportDef.label} · Saison {item.season || "—"}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Link
          to={publicPath}
          data-testid={`organizer-view-${kind}-${item.slug}`}
          className="w-9 h-9 rounded-full inline-flex items-center justify-center text-zinc-400 hover:text-white border border-white/10"
          title="Voir la page publique"
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </Link>
        <Link
          to={editPath}
          data-testid={`organizer-edit-${kind}-${item.slug}`}
          className="w-9 h-9 rounded-full inline-flex items-center justify-center text-black"
          style={{ background: "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)" }}
          title="Éditer"
        >
          <Pencil className="w-3.5 h-3.5" />
        </Link>
      </div>
    </motion.div>
  );
};

export default function OrganizerDashboard() {
  const { user, logout } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [championships, setChampionships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    Promise.all([
      TournamentsAPI.list().catch(() => []),
      ChampionshipsAPI.list().catch(() => []),
    ])
      .then(([ts, cs]) => {
        const mine = (arr) =>
          user.role === "admin" ? arr : arr.filter((x) => x.organizer_id === user.id);
        setTournaments(mine(ts));
        setChampionships(mine(cs));
      })
      .finally(() => setLoading(false));
  }, [user?.id, user?.role]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <div className="max-w-3xl mx-auto w-full px-4 md:px-8 py-10 md:py-16">
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="text-overline" style={{ color: "#D4AF37" }}>
            Espace Organisateur
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={logout}
            data-testid="organizer-logout"
            className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-zinc-300 hover:text-white rounded-full h-9 px-4 text-xs uppercase tracking-[0.18em] font-bold"
          >
            <LogOut className="w-3.5 h-3.5 mr-1.5" /> Déconnexion
          </Button>
        </div>
        <h1
          className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-none mb-2"
          data-testid="organizer-dashboard-title"
        >
          Mes <span style={{ color: "#D4AF37" }}>évènements</span>
        </h1>
        <p className="text-zinc-400 font-body mb-10">
          Bonjour {user?.name || user?.email} — gère tes tournois et championnats en toute
          autonomie : équipes, résultats, classement, photos, vidéos et sponsors.
        </p>

        <div className="flex flex-wrap gap-3 mb-10">
          <Link to="/admin/tournaments/new" data-testid="organizer-create-tournament">
            <Button
              className="text-black rounded-md px-5 h-11 font-body font-medium hover:opacity-90"
              style={{ backgroundColor: "#D4AF37" }}
            >
              <Plus className="w-4 h-4 mr-2" /> Créer un tournoi
            </Button>
          </Link>
          <Link to="/admin/championships/new" data-testid="organizer-create-championship">
            <Button
              variant="outline"
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white rounded-md px-5 h-11"
            >
              <Plus className="w-4 h-4 mr-2" /> Créer un championnat
            </Button>
          </Link>
          <Link to="/admin/scans" data-testid="organizer-scan-stats">
            <Button
              variant="outline"
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white rounded-md px-5 h-11"
            >
              <QrCode className="w-4 h-4 mr-2" /> Scans & vues
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="text-zinc-500 font-body">Chargement...</div>
        ) : (
          <div className="space-y-10">
            <section data-testid="organizer-tournaments">
              <div className="flex items-end justify-between mb-4">
                <h2 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight">
                  Mes tournois
                </h2>
                <span className="text-overline" style={{ color: "#D4AF37" }}>
                  {tournaments.length}
                </span>
              </div>
              {tournaments.length === 0 ? (
                <div className="border border-dashed border-white/10 rounded-xl p-8 text-center text-zinc-500 font-body text-sm">
                  Aucun tournoi pour le moment. Crée ton premier tournoi !
                </div>
              ) : (
                <div className="space-y-3">
                  {tournaments.map((t, i) => (
                    <EventCard key={t.id} item={t} kind="tournament" index={i} />
                  ))}
                </div>
              )}
            </section>

            <section data-testid="organizer-championships">
              <div className="flex items-end justify-between mb-4">
                <h2 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight">
                  Mes championnats
                </h2>
                <span className="text-overline" style={{ color: "#D4AF37" }}>
                  {championships.length}
                </span>
              </div>
              {championships.length === 0 ? (
                <div className="border border-dashed border-white/10 rounded-xl p-8 text-center text-zinc-500 font-body text-sm">
                  Aucun championnat pour le moment.
                </div>
              ) : (
                <div className="space-y-3">
                  {championships.map((c, i) => (
                    <EventCard key={c.id} item={c} kind="championship" index={i} />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-2">
          <Link
            to="/admin/associations"
            data-testid="organizer-link-associations"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white border border-white/15 bg-white/[0.04] rounded-full px-3 py-1.5 text-xs uppercase tracking-[0.18em] font-bold"
          >
            Associations
          </Link>
          <Link
            to="/admin/companies"
            data-testid="organizer-link-companies"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white border border-white/15 bg-white/[0.04] rounded-full px-3 py-1.5 text-xs uppercase tracking-[0.18em] font-bold"
          >
            Entreprises
          </Link>
        </div>

        <div className="mt-10">
          <CategoriesManager />
        </div>
      </div>
    </div>
  );
}
